import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import Alert from "../../components/common/Alert";
import Button from "../../components/common/Button";
import Card from "../../components/common/Card";
import FilterBar, {
  formatDateInputValue,
} from "../../components/common/FilterBar";
import PageHeader from "../../components/common/PageHeader";
import Pagination from "../../components/common/Pagination";
import { HorizontalBarList } from "../../components/common/ReportingCharts";
import Table from "../../components/common/Table";
import {
  useGetTopItemsCategoryMixQuery,
} from "../../store/api";
import {
  formatCurrency,
  formatNumber,
  getDefaultReportingFilters,
  getPresetDateRange,
} from "../../utils/reporting";
import { downloadExcelFile } from "../../utils/excelDownload";
import useClientPagination from "../../hooks/useClientPagination";

const TOP_ITEM_LIMIT_OPTIONS = [10, 20, 50];
const TOP_CATEGORY_LIMIT_OPTIONS = [
  { value: "10", label: "Top 10" },
  { value: "20", label: "Top 20" },
  { value: "all", label: "All" },
];

const TopItemsCategoryMix = () => {
  const navigate = useNavigate();
  const token = useSelector((state) => state.auth.token);
  const [isDownloading, setIsDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState("");
  const [draftFilters, setDraftFilters] = useState({
    ...getDefaultReportingFilters(),
    search: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({
    ...getDefaultReportingFilters(),
    search: "",
  });
  const [itemLimit, setItemLimit] = useState(10);
  const [categoryLimit, setCategoryLimit] = useState("10");

  const queryArgs = useMemo(() => {
    return {
      storeIds: appliedFilters.storeIds,
      from: appliedFilters.from,
      to: appliedFilters.to,
      limit: itemLimit,
    };
  }, [appliedFilters, itemLimit]);

  const { data, isLoading, isFetching, isError, error } =
    useGetTopItemsCategoryMixQuery(queryArgs);

  const currencyCode = data?.currencyCode || "USD";
  const datePresets = [
    { value: "today", label: "Today" },
    { value: "last7", label: "Last 7 Days" },
    { value: "last30", label: "Last 30 Days" },
  ];
  const activePreset =
    datePresets.find((preset) => {
      const range = getPresetDateRange(preset.value);
      return draftFilters.from === range.from && draftFilters.to === range.to;
    })?.value || "";
  const topItems = useMemo(() => {
    const items = Array.isArray(data?.topItems) ? [...data.topItems] : [];
    const normalizedSearch = (appliedFilters.search || "").trim().toLowerCase();

    const filteredItems = normalizedSearch
      ? items.filter((item) => {
          const haystack = [
            item?.itemName,
            item?.itemId,
            item?.categoryName,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();

          return haystack.includes(normalizedSearch);
        })
      : items;

    return filteredItems.sort(
      (left, right) =>
        Number(right?.salesAmount || 0) - Number(left?.salesAmount || 0),
    );
  }, [appliedFilters.search, data?.topItems]);

  const categoryMix = useMemo(() => {
    const categories = Array.isArray(data?.categoryMix)
      ? [...data.categoryMix]
      : [];
    const sortedCategories = categories.sort(
      (left, right) =>
        Number(right?.salesAmount || 0) - Number(left?.salesAmount || 0),
    );

    if (categoryLimit === "all") {
      return sortedCategories;
    }

    return sortedCategories.slice(0, Number(categoryLimit));
  }, [categoryLimit, data?.categoryMix]);

  const itemColumns = [
    { key: "index", label: "#" },
    { key: "itemName", label: "ITEM NAME" },
    { key: "quantity", label: "QUANTITY SOLD" },
    { key: "unitPrice", label: "UNIT PRICE" },
    { key: "salesAmount", label: "SALES AMOUNT" },
    { key: "orderCount", label: "ORDER COUNT" },
  ];
  const itemRows = topItems.map((item, index) => ({
    id: item.itemId || item.itemName || index,
    index: index + 1,
    itemName: item.itemName || "-",
    quantity: formatNumber(item.quantity),
    unitPrice: formatCurrency(item.unitPrice, currencyCode),
    salesAmount: formatCurrency(item.salesAmount, currencyCode),
    orderCount: formatNumber(item.orderCount),
  }));
  const categoryColumns = [
    { key: "index", label: "#" },
    { key: "categoryName", label: "CATEGORY NAME" },
    { key: "quantity", label: "QUANTITY SOLD" },
    { key: "salesAmount", label: "SALES AMOUNT" },
  ];
  const categoryRows = categoryMix.map((category, index) => ({
    id: category.categoryId || category.categoryName || index,
    index: index + 1,
    categoryName: category.categoryName || "-",
    quantity: formatNumber(category.quantity),
    salesAmount: formatCurrency(category.salesAmount, currencyCode),
  }));
  const itemChartHeight = Math.max(340, topItems.length * 34 + 80);
  const categoryChartHeight = Math.max(340, categoryMix.length * 34 + 80);
  const {
    page,
    totalCount,
    pageSize,
    paginatedItems: paginatedItemRows,
    setPage,
    setPageSize,
  } = useClientPagination(itemRows, { pageSize: 10 });

  const handleDownloadExcel = async () => {
    try {
      setIsDownloading(true);
      setDownloadError("");
      await downloadExcelFile({
        endpoint: "/admin/reporting/item-summary/export",
        queryParams: {
          storeIds: appliedFilters.storeIds,
          from: appliedFilters.from,
          to: appliedFilters.to,
          limit: itemLimit,
        },
        token,
        fallbackFileName: "central-item-summary.xlsx",
      });
    } catch (err) {
      if (err?.status === 401) {
        setDownloadError("Session expired. Please log in again.");
        navigate("/login");
      } else if (err?.isInvalidExcelResponse) {
        setDownloadError(
          "Export response is not an Excel file. Please verify API base URL or proxy configuration.",
        );
      } else {
        setDownloadError("Failed to download Excel. Please try again.");
      }
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Item Summary"
        subtitle="Item-level performance list across selected stores."
        actions={(
          <Button
            onClick={handleDownloadExcel}
            disabled={isDownloading}
            className="h-9 rounded-lg bg-indigo-600 px-4 text-sm font-semibold text-white shadow-[0_8px_16px_rgba(79,70,229,0.28)] hover:bg-indigo-700"
          >
            {isDownloading ? "Downloading..." : "Download"}
          </Button>
        )}
      />

      <Card>
        <div className="mb-6">
          <FilterBar
            startDate={draftFilters.from}
            endDate={draftFilters.to}
            onStartDateChange={(value) => {
              const nextFilters = {
                ...draftFilters,
                from: value ? formatDateInputValue(value) : "",
              };
              setDraftFilters(nextFilters);
              setAppliedFilters(nextFilters);
            }}
            onEndDateChange={(value) => {
              const nextFilters = {
                ...draftFilters,
                to: value ? formatDateInputValue(value) : "",
              };
              setDraftFilters(nextFilters);
              setAppliedFilters(nextFilters);
            }}
            showStore={false}
            presets={datePresets}
            activePreset={activePreset}
            onPresetChange={(presetKey) => {
              const range = getPresetDateRange(presetKey);
              const nextFilters = {
                ...draftFilters,
                from: range.from,
                to: range.to,
              };
              setDraftFilters(nextFilters);
              setAppliedFilters(nextFilters);
            }}
            showSearch
            search={draftFilters.search || ""}
            onSearchChange={(value) => {
              const nextFilters = {
                ...draftFilters,
                search: value,
              };
              setDraftFilters(nextFilters);
              setAppliedFilters(nextFilters);
            }}
            searchPlaceholder="Search items..."
            extraFilters={(
              <>
                <div className="min-w-[150px]">
                  <label className="mb-1 block text-xs font-medium text-slate-500">
                    Top Items
                  </label>
                  <select
                    value={itemLimit}
                    onChange={(event) =>
                      setItemLimit(Number(event.target.value))
                    }
                    className="h-9 w-full rounded border border-slate-200 bg-white px-3 text-sm text-slate-900"
                  >
                    {TOP_ITEM_LIMIT_OPTIONS.map((option) => (
                      <option key={option} value={option}>
                        Top {option}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="min-w-[170px]">
                  <label className="mb-1 block text-xs font-medium text-slate-500">
                    Top Categories
                  </label>
                  <select
                    value={categoryLimit}
                    onChange={(event) => setCategoryLimit(event.target.value)}
                    className="h-9 w-full rounded border border-slate-200 bg-white px-3 text-sm text-slate-900"
                  >
                    {TOP_CATEGORY_LIMIT_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              </>
            )}
            onReset={() => {
              const next = {
                ...getDefaultReportingFilters(),
                search: "",
              };
              setDraftFilters(next);
              setAppliedFilters(next);
              setItemLimit(10);
              setCategoryLimit("10");
            }}
            isLoading={isFetching}
          />
          {downloadError ? (
            <div className="mt-4">
              <Alert tone="error">{downloadError}</Alert>
            </div>
          ) : null}
        </div>

        {isError && (
          <Alert tone="error">
            {error?.data?.detail ||
              "Failed to load top items and category mix."}
          </Alert>
        )}

        <section className="mb-6 rounded-xl border border-slate-100 bg-white p-6">
          <div className="mb-5">
            <h2 className="text-lg font-semibold text-slate-900">
              Item Performance
            </h2>
          </div>
          {isLoading ? (
            <div className="grid gap-6">
              <div className="h-[340px] animate-pulse rounded-xl bg-slate-100" />
            </div>
          ) : topItems.length === 0 ? (
            <Alert tone="info">No chart data found for selected filters.</Alert>
          ) : (
            <div className="grid gap-6">
              <div>
                <div className="mb-3 text-sm font-semibold text-slate-700">
                  Sales By Item
                </div>
                <HorizontalBarList
                  items={topItems}
                  valueKey="salesAmount"
                  labelKey="itemName"
                  currencyCode={currencyCode}
                  listVariant="none"
                  barName="Sales"
                  barColor="#4f46e5"
                  chartHeight={itemChartHeight}
                  yAxisWidth={170}
                />
              </div>
              {/*
              <div>
                <div className="mb-3 text-sm font-semibold text-slate-700">
                  Quantity Sold By Item
                </div>
                <HorizontalBarList
                  items={topItems}
                  valueKey="quantity"
                  labelKey="itemName"
                  valueFormatter={(value) => formatNumber(value)}
                  listVariant="none"
                  barName="Quantity Sold"
                  barColor="#10b981"
                />
              </div>
              */}
            </div>
          )}
        </section>

        <section className="mb-6 rounded-xl border border-slate-100 bg-white p-6">
          <div className="mb-5">
            <h2 className="text-lg font-semibold text-slate-900">
              Category Mix
            </h2>
          </div>
          {isLoading ? (
            <div className="h-[340px] animate-pulse rounded-xl bg-slate-100" />
          ) : categoryMix.length === 0 ? (
            <Alert tone="info">
              No category mix data found for selected filters.
            </Alert>
          ) : (
            <div className="grid gap-6 xl:grid-cols-[minmax(0,1.3fr)_minmax(320px,0.7fr)]">
              <HorizontalBarList
                items={categoryMix}
                valueKey="salesAmount"
                labelKey="categoryName"
                currencyCode={currencyCode}
                listVariant="none"
                barName="Sales"
                barColor="#0f766e"
                chartHeight={categoryChartHeight}
                yAxisWidth={170}
              />
              <Table columns={categoryColumns} rows={categoryRows} />
            </div>
          )}
        </section>

        <section className="rounded-xl border border-slate-100 bg-white p-6">
          <div className="mb-5">
            <h2 className="text-lg font-semibold text-slate-900">Items</h2>
          </div>
          {isLoading ? (
            <div className="h-[240px] animate-pulse rounded-xl bg-slate-100" />
          ) : itemRows.length === 0 ? (
            <Alert tone="info">No items found for selected filters.</Alert>
          ) : (
            <>
              <Table columns={itemColumns} rows={paginatedItemRows} />
              <Pagination
                page={page}
                pageSize={pageSize}
                totalCount={totalCount}
                isLoading={isFetching}
                onPageChange={setPage}
                onPageSizeChange={(nextPageSize) => {
                  setPageSize(nextPageSize);
                }}
              />
            </>
          )}
        </section>
      </Card>
    </div>
  );
};

export default TopItemsCategoryMix;
